import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '../../lib/cn.ts';

/**
 * Button — shadcn-style. Owned in this repo; modify freely.
 *
 * Variants:
 *   primary    main CTA (filled, neutral-on-light)
 *   secondary  outline alternative
 *   ghost      no chrome — for toolbar buttons / icon buttons
 *   danger     destructive action confirmation
 *
 * Sizes:
 *   sm | md | lg | icon
 *
 * Use `asChild` to render any child element with the button styling
 * (e.g. <Button asChild><Link to="/x">go</Link></Button>).
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary:
          'bg-neutral-100 text-neutral-900 hover:bg-neutral-200 dark:bg-neutral-100 dark:text-neutral-900',
        secondary: 'border border-neutral-700 bg-transparent text-neutral-100 hover:bg-neutral-800',
        ghost: 'bg-transparent text-neutral-200 hover:bg-neutral-800 hover:text-neutral-100',
        danger: 'bg-red-600 text-white hover:bg-red-500',
      },
      size: {
        sm: 'h-8 px-3 text-xs',
        md: 'h-9 px-4',
        lg: 'h-11 px-6 text-base',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp ref={ref} className={cn(buttonVariants({ variant, size }), className)} {...props} />
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants };
